/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useContext } from 'react'

import { NormalPlan } from 'modules/billing/lib/prices'
import { PeriodUnit } from 'modules/onboarding/lib/pricing/types'

type PriceVariants = {
  totalPrice: number
  perSeatPrice: number
  fixedFeePrice: number
}

export type PricingBundleData = {
  currency: string
  period: PeriodUnit
  employeeSeats: number
  current: CurrentData
}

export type PricePeriods = {
  yearly: PriceVariants
  monthly: PriceVariants
}

export type CurrentData = PricePeriods & {
  selectedBundle: string
}

type PricingBundleContextType = {
  planType: string
  addons: NormalPlan[]
  currentData: CurrentData | undefined
  isYearly: boolean
}

type PricingBundleContextProviderProps = {
  selectedBundle: string
  isYearly: boolean
  extraAddons: NormalPlan[]
  children: React.ReactNode | React.ReactNode[]
  employeeSeats: number
  currentPlanPricing: PricePeriods
  planType: string
  currency: string
}

const PricingBundleContext = React.createContext<PricingBundleContextType>({
  planType: '',
  addons: [],
  currentData: undefined,
  isYearly: false,
})

export const PricingBundleProvider = ({
  children,
  selectedBundle,
  isYearly,
  extraAddons,
  employeeSeats,
  currentPlanPricing,
  planType,
  currency,
}: PricingBundleContextProviderProps) => {
  const pricingBundleData: PricingBundleData = {
    currency,
    period: isYearly ? 'year' : 'month',
    employeeSeats,
    current: {
      yearly: {
        totalPrice: currentPlanPricing.yearly.totalPrice,
        perSeatPrice: currentPlanPricing.yearly.perSeatPrice,
        fixedFeePrice: currentPlanPricing.yearly.fixedFeePrice,
      },
      monthly: {
        totalPrice: currentPlanPricing.monthly.totalPrice,
        perSeatPrice: currentPlanPricing.monthly.perSeatPrice,
        fixedFeePrice: currentPlanPricing.monthly.fixedFeePrice,
      },
      selectedBundle: selectedBundle,
    },
  }

  return (
    <PricingBundleContext.Provider
      value={{
        planType,
        addons: extraAddons,
        currentData: pricingBundleData.current,
        isYearly: pricingBundleData.period === 'year',
      }}
    >
      {children}
    </PricingBundleContext.Provider>
  )
}

export const usePricingBundleContext = () => useContext(PricingBundleContext)
